import * as m from '@staiter/i18n';
import type { CounterpartyKind } from '@staiter/shared';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Building2 } from 'lucide-react';
import { useState } from 'react';
import { PageHeader } from '~/components/data';
import { useToast } from '~/components/feedback';
import { Button, Input, Select } from '~/components/primitives';
import { type CounterpartyCreateBody, createCounterparty } from '~/lib/api/counterparties';
import { renderApiErrorMessage } from '~/lib/api/error-renderer';
import { useAuthStore } from '~/store/auth';
import shared from './_auth._shared.module.css';

/**
 * S14-FE.2 — Counterparty create form.
 * Sprint N+ 2026-05-23: FE-54 Cancel + sticky footer, FE-55 required `*`,
 * FE-58 better placeholder/hint, FE-59 conditional BIN/ИИН label by tax_status.
 */

/** FE-60 Sprint Q+: search params для designing-for-intent (return_to flow). */
type CpNewSearch = { return_to?: string; prefill_kind?: 'customer' | 'supplier' };
export const Route = createFileRoute('/_auth/counterparties/new')({
  validateSearch: (s: Record<string, unknown>): CpNewSearch => ({
    ...(typeof s.return_to === 'string' ? { return_to: s.return_to } : {}),
    ...(s.prefill_kind === 'customer' || s.prefill_kind === 'supplier'
      ? { prefill_kind: s.prefill_kind }
      : {}),
  }),
  component: CpNew,
});

function CpNew(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const search = Route.useSearch();
  const [form, setForm] = useState({
    // FE-60: prefill kind from query param (если пришли из /invoices/ar/new etc).
    kind: (search.prefill_kind ?? 'customer') as CounterpartyKind,
    name_ru: '',
    bin_iin: '',
    tax_status: 'vat_payer' as 'vat_payer' | 'simplified' | 'patent' | 'individual',
    default_currency: 'KZT' as 'KZT' | 'USD' | 'EUR' | 'RUB' | 'CNY',
    payment_terms_days: 30,
  });

  // FE-59: BIN для юрлиц (vat_payer/simplified), ИИН для ИП (individual/patent).
  const isIndividual = form.tax_status === 'individual' || form.tax_status === 'patent';
  const binIinLabel = isIndividual ? m.cp_column_iin() : m.cp_column_bin();

  // FE-61: smart defaults — kind=other скрывает tax_status (нерелевантно).
  const showTaxStatus = form.kind !== 'other';

  const createMut = useMutation({
    mutationFn: () => {
      const body: CounterpartyCreateBody = {
        kind: form.kind,
        name_ru: form.name_ru,
        name_kk: null,
        name_en: null,
        legal_form: null,
        bin_iin: form.bin_iin || null,
        tax_status: form.tax_status,
        contract_number: null,
        contract_date: null,
        payment_terms_days: form.payment_terms_days,
        default_currency: form.default_currency,
        bank_details: null,
        is_active: true,
      };
      return createCounterparty(body);
    },
    onSuccess: (r) => {
      toast.push({ variant: 'success', title: m.cp_toast_created() });
      // FE-60: если пришли из другой формы (return_to=) — назад с новым ID.
      if (search.return_to) {
        const url = new URL(search.return_to, window.location.origin);
        url.searchParams.set('counterparty_id', r.id);
        void navigate({ to: url.pathname + url.search });
        return;
      }
      void navigate({ to: '/counterparties/$id', params: { id: r.id } });
    },
    onError: (err: unknown) =>
      toast.push({
        variant: 'error',
        title: m.cp_toast_create_failed(),
        description: renderApiErrorMessage(err),
      }),
  });

  const activeCompany = useAuthStore((s) => s.user?.entity_name ?? null);

  return (
    <div>
      <PageHeader title={m.cp_button_new()} />
      {/* access-control (F6) — явный контекст компании, в которую создаётся запись. */}
      {activeCompany && (
        <div className={shared.formColumn480}>
          <div className={shared.companyContext}>
            <Building2 size={16} aria-hidden />
            <span>
              {m.cp_company_context()}: <strong>{activeCompany}</strong>
            </span>
          </div>
        </div>
      )}
      {/* FE-56 Sprint Q+: single-column layout 480px (per /ui-designer review) —
          5 полей в 2-col давали visual hole в row 3. Centered max-width. */}
      <div className={shared.formColumn480}>
        <Select
          label={m.cp_column_kind()}
          required
          value={form.kind}
          onChange={(e) => setForm({ ...form, kind: e.target.value as CounterpartyKind })}
          options={[
            { value: 'customer', label: m.cp_kind_customer() },
            { value: 'supplier', label: m.cp_kind_supplier() },
            { value: 'employee', label: m.cp_kind_employee() },
            { value: 'other', label: m.cp_kind_other() },
          ]}
        />
        <Input
          label={m.cp_column_name()}
          required
          value={form.name_ru}
          onChange={(e) => setForm({ ...form, name_ru: e.target.value })}
        />
        <Input
          label={binIinLabel}
          required
          value={form.bin_iin}
          onChange={(e) => setForm({ ...form, bin_iin: e.target.value })}
          placeholder={m.cp_bin_iin_placeholder()}
          hint={m.cp_bin_iin_hint()}
          inputMode="numeric"
          maxLength={12}
        />
        {/* FE-61: kind=other скрывает tax_status (нерелевантно — частное лицо). */}
        {showTaxStatus && (
          <Select
            label={m.cp_column_tax_status()}
            required
            value={form.tax_status}
            onChange={(e) =>
              setForm({
                ...form,
                tax_status: e.target.value as typeof form.tax_status,
              })
            }
            options={[
              { value: 'vat_payer', label: m.cp_tax_vat_payer() },
              { value: 'simplified', label: m.cp_tax_simplified() },
              { value: 'patent', label: m.cp_tax_patent() },
              { value: 'individual', label: m.cp_tax_individual() },
            ]}
          />
        )}
        <Select
          label={m.cp_column_currency()}
          required
          value={form.default_currency}
          onChange={(e) =>
            setForm({
              ...form,
              default_currency: e.target.value as typeof form.default_currency,
            })
          }
          options={[
            { value: 'KZT', label: 'KZT' },
            { value: 'USD', label: 'USD' },
            { value: 'EUR', label: 'EUR' },
            { value: 'RUB', label: 'RUB' },
            { value: 'CNY', label: 'CNY' },
          ]}
        />
      </div>
      <div className={shared.stickyFooter}>
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate({ to: '/counterparties' })}
        >
          {m.common_button_cancel()}
        </Button>
        <Button
          variant="primary"
          onClick={() => createMut.mutate()}
          loading={createMut.isPending}
          disabled={!form.name_ru || !form.bin_iin}
        >
          {m.common_button_create()}
        </Button>
      </div>
    </div>
  );
}
